// OneTrust consent helpers for tena-theme.
//
// Tena uses OneTrust (per `theme/snippets/one-trust-cookie-policy.liquid`) with
// `OtAutoBlock.js` enabled, which means any third-party script tagged for a
// non-essential category is auto-blocked until the visitor consents.
//
// This module gives the rest of the codebase a small, predictable surface for
// asking "do we have consent for category X?" and "tell me when consent
// changes" — without forcing every consumer to deal with OneTrust globals
// directly.
//
// Categories used in OneTrust convention:
//   C0001  Strictly Necessary  — always granted; no opt-out
//   C0002  Performance         — analytics, error tracking
//   C0003  Functional          — preferences, personalisation
//   C0004  Targeting           — advertising, marketing tracking
//
// `tena__selected-state` localStorage is treated as **strictly necessary**
// for #417 (essential to show the user correct stock availability for their
// state) — same justification used for existing `tena__sample-cart`,
// `tena__compare-product-list`, etc. The IP geolocation fetch is treated as
// **functional** (C0003) since it's a 3rd-party network call used to set a
// preference default.

// Read OneTrust's currently-active consent groups from the global it sets.
// Returns an array of group IDs e.g. ['C0001', 'C0002', 'C0003'].
function getActiveGroups() {
  if (typeof window === 'undefined') return [];
  const raw = window.OnetrustActiveGroups || '';
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
}

// Stores where OneTrust is intentionally NOT installed (e.g. staging). On
// these stores, treat consent as granted — there is no consent infrastructure
// to gate against, and waiting for OneTrust would block detection forever.
const ONETRUST_BYPASS_SHOPS = new Set([
  'tena-staging.myshopify.com',
]);

function isOnBypassShop() {
  if (typeof window === 'undefined' || !window.Shopify) return false;
  return ONETRUST_BYPASS_SHOPS.has(window.Shopify.shop);
}

// Secondary fail-open: if no OneTrust script tag is on the page at all (e.g.
// a dev theme without the snippet rendered), there's nothing to gate on.
// Still useful as a safety net for any non-listed store that drops the
// OneTrust integration in future. On stores where OneTrust IS deployed, the
// script tag is in DOM even before `window.OneTrust` resolves.
function isOneTrustDeployed() {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector('script[src*="cookielaw.org"], script[src*="onetrust"]'));
}

export function hasConsent(category) {
  if (!category) return false;
  if (category === 'C0001') return true; // strictly necessary — always granted
  // Stores explicitly whitelisted as no-OneTrust (staging) — bypass the gate.
  if (isOnBypassShop()) return true;
  // No OneTrust script in DOM — nothing to gate against.
  if (!isOneTrustDeployed()) return true;
  // OneTrust is deployed but its global hasn't resolved yet — err on the
  // side of NOT calling 3rd parties until the script has loaded.
  if (typeof window === 'undefined' || !window.OneTrust) return false;
  return getActiveGroups().includes(category);
}

// Subscribe to consent changes. OneTrust fires a `OneTrustGroupsUpdated`
// CustomEvent whenever the visitor accepts/rejects/changes preferences.
// The callback receives the new active-groups array.
//
// Returns an unsubscribe function so callers can clean up if needed.
export function onConsentChange(callback) {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  const handler = () => {
    try { callback(getActiveGroups()); } catch (e) { /* swallow — never crash on consent */ }
  };
  window.addEventListener('OneTrustGroupsUpdated', handler);
  return () => window.removeEventListener('OneTrustGroupsUpdated', handler);
}
